import { deepClone } from "@/utility"
import {
  BLANK_ACREAGE_VALUES,
  REGULAR_USER,
  STAFF_USER,
  OPEN,
  AVAILABLE,
  REQUESTED,
  CONFIRMED,
  LOCKED_FOR_PAYMENT,
  ANY_HARVEST_TYPE,
  SILAGE,
  REQUESTED_DELTA,
  CONFIRMED_DELTA,
} from "@/constants/contractGroups"
import { CROPS, CROP_DISPLAY_NAME } from "@/constants"

/**
 * keeping this shim in, unfortunately.
 *
 * we need a data structure change for field-level data to do this properly
 * so for now, corn (silage) will act as its own crop, all other harvest types don't matter
 */
export const getProcessedHarvestType = (cropIds, harvestType) => {
  if (harvestType == null) return ANY_HARVEST_TYPE
  return cropIds.find(cId => cId == 4) != null && harvestType.trim().toLowerCase() == SILAGE
    ? SILAGE
    : ANY_HARVEST_TYPE
}

export const formatSupplySheds = (supplySheds) => {
  return supplySheds.reduce((accum, curr) => {
    const harvest_type = getProcessedHarvestType(curr['crop_ids'], curr['harvest_type'])
    const formattedShed = {
      id: curr['id'],
      name: curr['name'],
      priority: curr['priority'],
      geo_ids: curr['geo_ids'],
      crops: curr['crop_ids'].map(id => id == 4 && harvest_type == SILAGE ? 'Corn Silage' : CROP_DISPLAY_NAME[id]),
      crop_ids: curr['crop_ids'],
      harvest_type,
      state_name: curr['state_name'],
      total_available: curr['total_available'],
      total_requested: curr['total_requested'],
      total_enrolled: curr['total_enrolled']
    }
    formattedShed[REQUESTED_DELTA] = {}
    formattedShed[CONFIRMED_DELTA] = {}
    accum.push(formattedShed)
    return accum
  }, [])
}

export const validateClientProfiles = profile => {
  const modifiedProfile = structuredClone(profile)

  let isValidated = true

  // user is optional
  // street_address_2 is optional
  if (profile["first_name"] == null || profile["first_name"] == "")
    isValidated = false
  if (profile["last_name"] == null || profile["last_name"] == "")
    isValidated = false
  if (profile["title"] == null || profile["title"] == "") isValidated = false
  if (
    profile["relationship_to_primary_contact"] == null
    || profile["relationship_to_primary_contact"] == ""
  )
    isValidated = false
  if (profile["phone_number"] == null || profile["phone_number"] == "")
    isValidated = false
  if (profile["poc_email"] == null || profile["poc_email"] == "")
    isValidated = false
  if (profile["street_address_1"] == null || profile["street_address_1"] == "")
    isValidated = false
  if (profile["city"] == null || profile["city"] == "") isValidated = false
  if (profile["state"] == null || profile["state"] == "") isValidated = false
  if (profile["zip_code"] == null || profile["zip_code"] == "")
    isValidated = false
  if (profile["country"] == null || profile["country"] == "")
    isValidated = false

  //WARNING We are ignoring all validations
  isValidated = true

  modifiedProfile["validated"] = isValidated
  return modifiedProfile
}

/**
 * 1. validates contact profile information
 * 2. sets up state for enabling/disabled yes/no buttons on detail pages, as well as
 * aggregates fss metadata and acreage into group acreage state summaries
 * 3. normalize harvest types, silage and corn are strict, all others can intermingle
 * 4. sets up caution modal for if a user is unenrolling confirmed acres
 * 5. enable/disable enroll/unenroll actions based on user permission level
 * 6. assign requestable supply sheds to individual fss
 * and apply fss acreage to supply sheds for display in the supply shed rollup table
 * 7. apply fss acreage to supply sheds for display in the supply shed rollup table
 */
export const processGroupData = (supplySheds, groupData) => {
  const clonedSupplySheds = deepClone(supplySheds)
  const supplyShedOverages = []

  const groupIdSupplyShedTemplate = {}
  for (const groupId in groupData) groupIdSupplyShedTemplate[groupId] = 0

  for (const supplyShed of clonedSupplySheds) {
    for (const key of [REQUESTED_DELTA, CONFIRMED_DELTA]) supplyShed[key] = { ...groupIdSupplyShedTemplate }

    // build our overages
    for (const cropId of supplyShed['crop_ids']) {
      let foundOverageSupplyShed = supplyShedOverages.find(({ crop_ids, harvest_type }) => crop_ids.includes(cropId) && harvest_type == supplyShed['harvest_type'])
      if (foundOverageSupplyShed == null) {
        foundOverageSupplyShed = {
          "id": -1,
          "name": `${CROP_DISPLAY_NAME[cropId]}${supplyShed['harvest_type'] != 'any' ? ` ${supplyShed['harvest_type']}` : ''} Overage`,
          "priority": -1,
          "geo_ids": [...supplyShed['geo_ids']],
          "crops": [cropId == 4 && supplyShed['harvest_type'] == 'silage' ? 'Corn Silage' : CROP_DISPLAY_NAME[cropId]],
          "crop_ids": [cropId],
          "harvest_type": supplyShed['harvest_type'],
          "total_available": 0,
          "total_requested": 0,
          "total_enrolled": 0,
          "group_requested": { ...groupIdSupplyShedTemplate },
          "group_confirmed": { ...groupIdSupplyShedTemplate },
          "requested_delta": { ...groupIdSupplyShedTemplate },
          "confirmed_delta": { ...groupIdSupplyShedTemplate }
        }

        supplyShedOverages.push(foundOverageSupplyShed)
      }

      // for each overage, make sure each relevant geoid is used
      for (const geoid of supplyShed['geo_ids']) {
        if (!foundOverageSupplyShed['geo_ids'].includes(geoid)) foundOverageSupplyShed['geo_ids'].push(geoid)
      }
    }

    // spot for shims, right now we're still merging wheat + winter wheat
    const toRemove = []
    for (const overageSupplyShedIdx in supplyShedOverages) {
      const overageSupplyShed = supplyShedOverages[overageSupplyShedIdx]
      if (overageSupplyShed['crop_ids'].length == 1 && overageSupplyShed['crop_ids'][0] == CROPS.WinterWheat) {
        // see if we have a wheat overage, and if we do combine it
        const foundWheatOverage = supplyShedOverages.find(({ crop_ids }) => crop_ids.length == 1 && crop_ids[0] == CROPS.Wheat)
        if (foundWheatOverage != null) {
          toRemove.push(overageSupplyShedIdx)

          foundWheatOverage['crop_ids'].push(CROPS.WinterWheat)
          foundWheatOverage['crops'].push(CROP_DISPLAY_NAME[CROPS.WinterWheat])
          for (const geoid of overageSupplyShed['geo_ids']) {
            if (!foundWheatOverage['geo_ids'].includes(geoid)) foundWheatOverage['geo_ids'].push(geoid)
          }
        }
      }
    }

    for (const removeIdx of toRemove) {
      supplyShedOverages.splice(removeIdx, 1)
    }
  }

  const overageSupplySheds = clonedSupplySheds.filter(({ id }) => id == -1)
  if (overageSupplySheds.length == 0) {
    // if we haven't added any overage supply sheds yet, do so
    clonedSupplySheds.push(...supplyShedOverages)
  }

  const processedGroupData = {}
  for (const [
    groupId,
    {
      name, geo_ids, status, notes, is_pooling_entity, w9_file, updated_at,
      client_crop_region_map, client_names_in_group, fieldsupply, evidencing_links,
      contract_outdated, can_generate_contract, has_maxcap, enrollment,
      cp_profiles, grower_profiles, landowner_profiles,
    },
  ] of Object.entries(groupData)) {
    // don't show unassigned pooling entities
    if (is_pooling_entity) continue

    // for all overage supply sheds, make sure we reset this group's request/confirmed mappings
    for (const overageSupplyShed of clonedSupplySheds) {
      if (overageSupplyShed['id'] != -1) continue
      overageSupplyShed['group_requested'][groupId] = 0
      overageSupplyShed['group_confirmed'][groupId] = 0
    }

    // 1. validate contact profile info
    const validatedCPProfiles = cp_profiles.map(validateClientProfiles)
    const validatedGrowerProfiles = grower_profiles.map(validateClientProfiles)
    // landowner profiles..?

    // 2. sets up state for enabling/disabled yes/no buttons on detail pages, as well as
    // aggregates fss metadata and acreage into group acreage state summaries
    const { confirmedFSSGrouping, lockedFSSGrouping } = fieldsupply.reduce((accum, curr) => {
      const processedHarvestType = getProcessedHarvestType([curr['crop_id']], curr['harvest_type'])

      for (const [groupingStatus, groupingKey] of [[CONFIRMED, 'confirmedFSSGrouping'], [LOCKED_FOR_PAYMENT, 'lockedFSSGrouping']]) {
        if (curr['status'] == groupingStatus) {
          const found = accum[groupingKey].find(c => c['crop_id'] == curr['crop_id'] && c['harvest_type'] == processedHarvestType)
          if (found == null) accum[groupingKey].push({ crop_id: curr['crop_id'], harvest_type: processedHarvestType })
        }
      }
      return accum
    }, { confirmedFSSGrouping: [], lockedFSSGrouping: [] })

    const confirmedFSSToBeUnenrolled = {}
    const acreageValues = {}
    for (const clientId in client_crop_region_map) {
      acreageValues[clientId] = {}
      confirmedFSSToBeUnenrolled[clientId] = {}

      for (const cropId in client_crop_region_map[clientId]) {
        acreageValues[clientId][cropId] = {}
        confirmedFSSToBeUnenrolled[clientId][cropId] = {}

        for (const harvestType in client_crop_region_map[clientId][cropId]) {
          const processedHarvestType = getProcessedHarvestType([cropId], harvestType)
          acreageValues[clientId][cropId][processedHarvestType] = {}
          confirmedFSSToBeUnenrolled[clientId][cropId][processedHarvestType] = []

          for (const geoid in client_crop_region_map[clientId][cropId][processedHarvestType]) {
            acreageValues[clientId][cropId][processedHarvestType][geoid] = deepClone(BLANK_ACREAGE_VALUES)
          }
        }
      }
    }

    fieldsupply.forEach(fss => {
      const { status, status_change_pending, acreage, field_name, crop_id, geoid, harvest_type, client_id } = fss

      if (!Array.isArray(fss['practices'])) fss['practices'] = []

      // 4. normalize harvest types, silage and corn are strict, all others can intermingle
      const processedHarvestType = getProcessedHarvestType([crop_id], harvest_type)
      fss['crop'] = crop_id == 4 && harvest_type == SILAGE ? 'Corn Silage' : CROP_DISPLAY_NAME[crop_id]

      // 5. set up caution modal for if a user is unenrolling confirmed acres
      // (and apply the fss acres to the formatted object defined above outside the forEach)
      fss[REGULAR_USER] = true
      fss[STAFF_USER] = true

      if (status == CONFIRMED && status_change_pending) {
        confirmedFSSToBeUnenrolled[client_id][crop_id][processedHarvestType].push({
          id: fss['id'],
          field_name,
          crop_id,
          harvest_type: processedHarvestType,
          geoid,
          acreage
        })
      }

      // 6. enable/disable enroll/unenroll actions based on user permission level
      // if:
      //    status is locked_for_payment
      //    OR status of a shared fss (see above) is locked_for_payment
      // then exclude this FSS from submissions from both CPs and CSMs
      //    OR it's CP view + the current status of the fss is confirmed
      //    OR it's CP view + a shared fss (see above) is confirmed
      // then exclude this FSS from submissions from CPs only
      const foundConfirmed = confirmedFSSGrouping.find(c => c['crop_id'] == fss['crop_id'] && c['harvest_type'] == processedHarvestType)
      const foundLocked = lockedFSSGrouping.find(c => c['crop_id'] == fss['crop_id'] && c['harvest_type'] == processedHarvestType)
      if (fss['status'] == LOCKED_FOR_PAYMENT || foundLocked != null) {
        fss[REGULAR_USER] = false
        fss[STAFF_USER] = false
      }
      else if (fss['status'] == CONFIRMED || foundConfirmed != null) {
        fss[REGULAR_USER] = false
        fss[STAFF_USER] = true
      }

      if (fss[STAFF_USER]) {
        acreageValues[client_id][crop_id][processedHarvestType][geoid][status][status_change_pending][STAFF_USER]['acreage'] += acreage
        acreageValues[client_id][crop_id][processedHarvestType][geoid][status][status_change_pending][STAFF_USER]['numFields'] += 1
      }

      if (fss[REGULAR_USER]) {
        acreageValues[client_id][crop_id][processedHarvestType][geoid][status][status_change_pending][REGULAR_USER]['acreage'] += acreage
        acreageValues[client_id][crop_id][processedHarvestType][geoid][status][status_change_pending][REGULAR_USER]['numFields'] += 1
      }

      // 7. assign requestable supply sheds to individual fss
      //    and apply fss acreage to supply sheds for display in the supply shed rollup table
      //    and apply fss acreage to overages where applicable
      // find which fss go to which supplyShed based on supplyShed's priority property
      // plus some fallbacks in case they aren't set
      fss['supplyShedIDs'] = clonedSupplySheds
        .filter(({ id, geo_ids, crops, total_available, total_requested }) =>
          id != -1
          && total_requested < total_available
          && geo_ids.includes(fss['geoid'])
          && crops.includes(fss['crop'])
        )
        .sort((a, b) => (b['total_requested'] / b['total_available']) / (a['total_requested'] / a['total_available']))
        .sort((a, b) => b['priority'] - a['priority'])
        .map(supplyShed => supplyShed['id'])

      fss['is_overage'] = false

      if (
        (fss['supplyShedIDs'].length == 0 && fss['supply_shed_id'] == null)
        || (fss['supplyShedIDs'].length > 0 && fss['supply_shed_id'] == null && fss['status'] == REQUESTED)
      ) {
        const foundOverageSupplyShed = clonedSupplySheds.find(supplyShed =>
          supplyShed['id'] == -1 && supplyShed['crop_ids'].includes(crop_id) && supplyShed['harvest_type'] == harvest_type
        )

        if (foundOverageSupplyShed != null) {
          if (status == OPEN || status == AVAILABLE) {
            if (status_change_pending) {
              fss['is_overage'] = true
              foundOverageSupplyShed[REQUESTED_DELTA][groupId] += acreage
            }
          }
          if (status == REQUESTED) {
            fss['is_overage'] = true
            if (status_change_pending) foundOverageSupplyShed[REQUESTED_DELTA][groupId] -= acreage
            else foundOverageSupplyShed['group_requested'][groupId] += acreage
          }
          if (status == CONFIRMED) {
            fss['is_overage'] = true
            if (status_change_pending) foundOverageSupplyShed[CONFIRMED_DELTA][groupId] -= acreage
            else foundOverageSupplyShed['group_confirmed'][groupId] += acreage
          }
        }
      }

      fss['proposed_supply_shed_id'] = fss['supplyShedIDs'][0]

      if (fss['supplyShedIDs'].length > 0 || (fss['supplyShedIDs'].length == 0 && fss['supply_shed_id'] != null)) {
        // if this fss has possible supply sheds, see if we're implementing a pending change or not
        // if requested/confirmed/locked for payment, this fss should be in a supply shed's requested fss, pending removal
        // if open/available, this fss not be in a supply shed, pending addition to one
        const proposedSupplyShed = clonedSupplySheds.find(({ id }) => id == fss['proposed_supply_shed_id'])
        const assignedSupplyShed = clonedSupplySheds.find(({ id }) => id == fss['supply_shed_id'])

        if (fss['status_change_pending']) {
          if ((fss['status'] == OPEN || fss['status'] == AVAILABLE)) {
            if (proposedSupplyShed != null) {
              // open/available w/ pending change means pending addition to the proposed
              proposedSupplyShed[REQUESTED_DELTA][groupId] += fss['acreage']
            }
          }

          if (fss['status'] == REQUESTED || fss['status'] == CONFIRMED || fss['status'] == LOCKED_FOR_PAYMENT) {
            if (assignedSupplyShed != null) {
              const key = fss['status'] == REQUESTED ? REQUESTED_DELTA : CONFIRMED_DELTA
              // requested/confirmed/locked w/ pending change means pending removal to the current assigned
              assignedSupplyShed[key][groupId] -= fss['acreage']
            }
          }
        }
      }
    })

    processedGroupData[groupId] = {
      name,
      notes,
      geo_ids,
      w9_file,
      updated_at,
      evidencing_links,
      client_crop_region_map,
      client_names_in_group,
      status,
      contract_outdated,
      can_generate_contract,
      has_maxcap,
      enrollment,
      fieldsupply,
      acreageValues,
      confirmedFSSToBeUnenrolled,
      cp_profiles: validatedCPProfiles,
      grower_profiles: validatedGrowerProfiles,
      landowner_profiles,
    }
  }

  for (const overageSupplyShed of clonedSupplySheds) {
    if (overageSupplyShed['id'] != -1) continue
    overageSupplyShed['total_requested'] = Object.values(overageSupplyShed['group_requested']).reduce((accum, curr) => accum + curr, 0)
    overageSupplyShed['total_confirmed'] = Object.values(overageSupplyShed['group_confirmed']).reduce((accum, curr) => accum + curr, 0)
  }

  return { processedGroupData, supplySheds: clonedSupplySheds }
}
